import { cn } from '@/utils';
import { StarIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import React from 'react';

const ratingVariants = {
  default: {
    star: 'text-[#FEC74B]',
    emptyStar: 'text-[#E4E4E4]',
  },
  destructive: {
    star: 'text-error-500',
    emptyStar: 'text-error-200',
  },
  yellow: {
    star: 'text-[#EDBD14]',
    emptyStar: 'text-[#E4E4E4]',
  },
};

type IconComponentProps = {
  size?: number;
  className?: string;
};

function DefaultStarIcon({ size, className }: IconComponentProps) {
  return (
    <HugeiconsIcon icon={StarIcon} className={cn('size-[9px]', className)} />
  );
}

interface RatingsProps extends React.HTMLAttributes<HTMLDivElement> {
  rating: number;
  totalStars?: number;
  size?: number;
  fill?: boolean;
  Icon?: React.ComponentType<IconComponentProps>;
  variant?: keyof typeof ratingVariants;
}

const Ratings = ({ ...props }: RatingsProps) => {
  const {
    rating,
    totalStars = 5,
    size = 20,
    fill = true,
    Icon = DefaultStarIcon,
    variant = 'default',
    ...divProps
  } = props;

  const fullStars = Math.floor(rating);
  const partialStar =
    rating % 1 > 0 ? (
      <PartialStar
        fillPercentage={rating % 1}
        size={size}
        className={cn(ratingVariants[variant].star)}
        Icon={Icon}
      />
    ) : null;

  return (
    <div className={cn('flex items-center gap-2')} {...divProps}>
      {[...Array(fullStars)].map((_, i) => (
        <Icon
          key={i}
          size={size}
          className={cn(
            fill ? 'fill-current' : 'fill-transparent',
            ratingVariants[variant].star,
          )}
        />
      ))}
      {partialStar}
      {[...Array(totalStars - fullStars - (partialStar ? 1 : 0))].map((_, i) => (
        <Icon
          key={i + fullStars + 1}
          size={size}
          className={cn(ratingVariants[variant].emptyStar)}
        />
      ))}
    </div>
  );
};

interface PartialStarProps {
  fillPercentage: number;
  size: number;
  className?: string;
  Icon: React.ComponentType<IconComponentProps>;
}

const PartialStar = ({
  fillPercentage,
  size,
  className,
  Icon,
}: PartialStarProps) => {
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Icon size={size} className={cn('fill-transparent', className)} />
      <div
        style={{
          position: 'absolute',
          top: 0,
          overflow: 'hidden',
          width: `${fillPercentage * 100}%`,
        }}
      >
        <Icon size={size} className={cn('fill-current', className)} />
      </div>
    </div>
  );
};

export { Ratings };
